import React, { useCallback, useEffect } from 'react';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  BackButton,
  Button,
  OfferPriceDetails,
  useBoolModalState,
} from 'components';
import {
  getInvoiceUrl,
  Invoice,
  InvoiceStatus,
  OfferType,
  useBillingInformationsByOrga,
  useCurrentOrganizationId,
  useMe,
  useOffer,
} from '@app/api';
import { LoginContent } from '../../components/LoginModal/LoginContent';
import { SignupContent } from '../../components/LoginModal/SignupContent';
import { PaymentForm } from './PaymentForm';
import { useTranslation } from 'react-i18next';
import { G_CALENDAR_LINK } from 'components/GoogleCalendar/gCalendar';
import { useLocale } from '@app/i18n';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    minHeight: '100%',
    [theme.media.tablet]: {
      gridTemplateColumns: '1fr',
    },
  },
  pricePanel: {
    position: 'relative',
    flex: 1,
    backgroundColor: theme.colors.grayBackground,
    display: 'flex',
    justifyContent: 'center',
  },
  back: {
    position: 'fixed',
    top: theme.marginBase * 4,
    left: theme.marginBase * 4,
  },
  priceLayout: {
    position: 'fixed',
    top: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',

    [theme.media.tablet]: {
      position: 'relative',
    },

    backgroundColor: theme.colors.grayBackground,
    padding: [[theme.marginBase * 8, theme.marginBase * 6]],
  },
  priceContent: {
    minWidth: 520,
    [theme.media.tablet]: {
      minWidth: 'auto',
    },
  },
  formPanel: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
  },
  title: {
    ...theme.fonts.h3,
    marginBottom: theme.marginBase * 4,
  },
  subTitle: {
    ...theme.fonts.h4,
    marginBottom: theme.marginBase,
  },
  rightTitle: {
    ...theme.fonts.h3,
    marginBottom: theme.marginBase * 4,
    marginTop: theme.marginBase * 2,
  },
  loginContent: {
    width: '100%',
  },
  list: {
    paddingLeft: theme.marginBase * 3,
    ...theme.fonts.caption,
    color: theme.colors.inkSecondary,
  },
  offers: {
    marginBottom: theme.marginBase * 4,
  },
  formContent: {
    maxWidth: theme.maxContentWidth.largeForm,
    width: '100%',
    padding: [[theme.marginBase * 4, theme.marginBase * 6]],

    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    justifyContent: 'center',
    minHeight: '100%',
  },
  bannerContainer: {
    ...theme.fonts.label,
    marginTop: theme.marginBase * 6,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.marginBase * 4,
    padding: theme.marginBase * 2,
    backgroundColor: theme.colors.blueMuted,
    borderRadius: theme.borderRadius.std,
    width: '100%',
  },
}));

export const PaymentPage = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [applyVat, setApplyVat] = React.useState(true);

  const [logingIn, goToLogin, goToSignup] = useBoolModalState(false);
  const { data: me } = useMe(true);
  useLocale(me?.locale);
  const logged = !!me;

  const { search } = useLocation();
  const offerId = new URLSearchParams(search).get('offer') || undefined;
  const queryData = useOffer(offerId);
  const { data: offer } = queryData;

  const organizationId = useCurrentOrganizationId();
  const { data: billingInformation } =
    useBillingInformationsByOrga(organizationId);

  const onSuccess = useCallback(
    (invoice: Invoice) => {
      if (invoice.status === InvoiceStatus.OPEN) {
        window.open(getInvoiceUrl(invoice), '_blank', 'noopener,noreferrer');
      }

      navigate('/billing-dashboard');
    },
    [navigate],
  );
  const backUrl =
    offer?.type === OfferType.SPECIAL_OFFER
      ? logged
        ? '/special-offer'
        : '/special-offer-annon'
      : '/billing-dashboard';

  useEffect(() => {
    if (!offerId) {
      navigate('/');
    }
  }, [offerId]);

  if (!offerId) {
    return null;
  }

  return (
    <div className={classes.container}>
      <div className={classes.pricePanel}>
        <BackButton color="ink" className={classes.back} to={backUrl} />
        <div className={classes.priceLayout}>
          <div className={classes.priceContent}>
            <h1 className={classes.title}>
              {t('billing.paymentForm.title', {
                name: offer?.name || '',
              })}
            </h1>
            <h2 className={classes.subTitle}>
              {t('billing.paymentForm.content')}
            </h2>

            {offer && (
              <OfferPriceDetails
                offer={offer}
                applyVat={applyVat}
                organizationId={organizationId}
              />
            )}

            <div className={classes.bannerContainer}>
              {t('billing.paymentForm.contactUs')}
              <Button
                href={G_CALENDAR_LINK}
                target="_blank"
                text={t('billing.paymentForm.contactUsButton')}
                small
              />
            </div>
          </div>
        </div>
      </div>
      <div className={classes.formPanel}>
        <div className={classes.formContent}>
          {logged && billingInformation ? (
            <div>
              <PaymentForm
                offerId={offerId}
                billingInformation={billingInformation}
                onShouldApplyVatChange={setApplyVat}
                onSuccess={onSuccess}
              />
            </div>
          ) : logingIn ? (
            <div>
              <h2 className={classes.rightTitle}>
                {t('billing.paymentForm.login')}
              </h2>
              <LoginContent onSignupClick={goToSignup} />
            </div>
          ) : (
            <div>
              <h2 className={classes.rightTitle}>
                {t('billing.paymentForm.signup')}
              </h2>
              <SignupContent
                onLoginClick={goToLogin}
                trackContext="checkout"
                noPromoCode
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
